import type { Feature, MultiPolygon, Polygon } from "geojson";

import type { AppLocale } from "@/lib/locale/resolve-effective-locale";
import { WarningLevel } from "@/lib/warning-level";

/** `issued` is a published warning; `chance` is a kansverwachting. */
export type RiskPictureKind = "issued" | "chance";

export type RiskPictureLevel = "yellow" | "orange" | "red";

/** Risk-picture level as the app-wide `WarningLevel` (i18n keys, toolbar colors). */
export const RISK_PICTURE_WARNING_LEVEL: Record<RiskPictureLevel, WarningLevel> = {
  yellow: WarningLevel.Yellow,
  orange: WarningLevel.Orange,
  red: WarningLevel.Red,
};

export interface RiskPictureItem {
  id: string;
  phenomenon: string;
  kind: RiskPictureKind;
  /** Omitted when the part has no yellow/orange/red color. */
  level?: RiskPictureLevel;
  /** Wire bucket string, e.g. "30-60". */
  probabilityRange?: string;
  /** Dutch headline from the warning set. */
  headline?: string;
  description?: string;
  detail?: string;
  /**
   * Name of this part's polygon. A bare `area_description` string is copied
   * onto both languages.
   */
  areaDescription?: RiskPictureAreaDescription;
  /** ISO timestamps of the part, used by the map popup. */
  onset?: string;
  expires?: string;
  area: Feature<Polygon | MultiPolygon>;
}

export interface RiskPictureAreaDescription {
  nl?: string;
  en?: string;
}

/** English uses `en`, anything else `nl`. Missing text falls back to the other language. */
export const riskPictureAreaDescription = (
  description: RiskPictureAreaDescription | undefined,
  locale: AppLocale,
): string | undefined => {
  if (!description) return undefined;
  if (locale === "en") return description.en ?? description.nl;
  return description.nl ?? description.en;
};

export interface RiskPictureDay {
  /** Local calendar date, YYYY-MM-DD. */
  date: string;
  items: RiskPictureItem[];
}

export interface RiskPictureCopy {
  title: string;
  description: string;
}

export interface RiskPictureModel {
  days: RiskPictureDay[];
  /** Seasonal catalog, then phenomena outside it that have a warning on one of the days. */
  phenomena: string[];
}
